import React from 'react';

const sections = [
  {
    title: '1. Acceptance of Terms',
    paragraphs: [
      'By accessing, browsing, registering for, purchasing, activating, or using the Services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not access or use the Services.',
      'You agree that these Terms form a binding legal agreement between you and CheckInCare. If you use the Services on behalf of another person or entity, you represent and warrant that you are authorized to accept these Terms on their behalf, and references to "you" include that person or entity.'
    ]
  },
  {
    title: '2. Eligibility',
    paragraphs: [
      'You must be at least eighteen (18) years old and legally capable of entering into a binding contract to use the Services. By using the Services, you represent and warrant that you meet these eligibility requirements.',
      'If the Services are used for the benefit of another person, including a device user who may not manage the account personally, the person creating or administering the account represents and warrants that they have all necessary authority and permissions to do so.'
    ]
  },
  {
    title: '3. Description of Services',
    paragraphs: [
      'CheckInCare provides a subscription-based connected device and related software services intended to support wellness check-in workflows for individuals living independently.'
    ],
    bullets: [
      'A physical device that allows a user to press a button to record a daily check-in',
      'Cloud-based data processing and storage',
      'Web and mobile caregiver interfaces',
      'SMS and email notifications to designated caregivers',
      'Account, billing, and support functionality'
    ]
  },
  {
    title: '4. Important Safety Notice; No Medical or Emergency Use',
    paragraphs: [
      'CheckInCare is not a medical device, is not intended for diagnosis, cure, mitigation, treatment, or prevention of any disease or medical condition, and is not regulated or marketed as a medical device.',
      'CheckInCare is not a 911 service, emergency response service, emergency communications service, alarm service, or monitored life-safety service. CheckInCare does not contact 911, police, fire, EMS, emergency responders, or dispatch centers.',
      'You must not rely on the Services for life-critical, safety-critical, medical, emergency, or time-sensitive situations. The Services are not a substitute for emergency response systems, personal supervision, regular wellness visits, professional caregiving, medical monitoring, emergency alert systems, or any other safety protocol.',
      'Notifications generated by the Services may be delayed, misdirected, corrupted, duplicated, incomplete, or fail to be sent, transmitted, received, viewed, or acted upon.',
      'CheckInCare does not guarantee that any caregiver will receive, see, review, acknowledge, or respond to any alert, notification, or communication, or that any action will be taken in response to any missed check-in or other event.'
    ]
  },
  {
    title: '5. Account Registration and Responsibilities',
    paragraphs: [
      'To use certain Services, you may be required to create an account and provide accurate, current, and complete information. You agree to keep your account information updated at all times.'
    ],
    bullets: [
      'Maintaining the confidentiality of your login credentials',
      'Restricting access to your account and devices',
      'All activity occurring under your account',
      'Ensuring that designated caregivers contact information is accurate and current',
      'Ensuring that all persons whose information you provide have authorized you to provide that information',
      'Promptly notifying CheckInCare of any suspected unauthorized use, compromise, or security breach'
    ]
  },
  {
    title: '6. Subscription, Billing, Auto-Renewal, and Cancellation',
    paragraphs: [
      'Certain Services are offered on a subscription basis. By purchasing a subscription, you agree to pay all applicable fees, taxes, shipping charges, and other charges disclosed at the time of purchase.',
      'Unless otherwise stated, subscriptions automatically renew at the end of each billing cycle until canceled. You authorize CheckInCare and its payment processors to charge your selected payment method on a recurring basis for all applicable subscription fees and taxes without further notice, unless required by law.',
      'You may cancel your subscription at any time through your account or any method we make available. Unless otherwise required by law or expressly stated by CheckInCare in writing, cancellations take effect at the end of the then-current billing period, and fees already paid are non-refundable.'
    ]
  },
  {
    title: '7. Device Use and Limitations',
    paragraphs: [
      'You agree to use the device only in accordance with all instructions, documentation, and safety guidance provided by CheckInCare.',
      'The device may fail due to battery depletion, hardware malfunction, misuse, tampering, environmental conditions, connectivity limitations, software defects, or ordinary wear and tear.',
      'You must not modify, disassemble, reverse engineer, interfere with, or misuse the device except to the extent such restriction is prohibited by applicable law.'
    ]
  },
  {
    title: '8. Service Availability and Third-Party Dependencies',
    paragraphs: [
      'The Services are provided on an "as is" and "as available" basis. CheckInCare does not guarantee continuous, uninterrupted, secure, or error-free availability of the Services.',
      'The Services rely on third-party infrastructure and providers, including hosting providers, internet service providers, cellular carriers, email providers, and messaging vendors such as Twilio. We are not responsible for outages, degradation, routing failures, filtering, throttling, carrier restrictions, latency, delivery failures, or other performance issues caused by third-party services or infrastructure.'
    ]
  },
  {
    title: '9. SMS Messaging Terms',
    paragraphs: [
      'By providing a mobile phone number and opting in to receive text messages from CheckInCare, you represent and warrant that you have the prior express consent of the applicable recipient to receive SMS messages from CheckInCare related to the Services, including service alerts, missed check-in notices, account-related messages, operational notifications, and other transactional or informational messages.',
      'Message frequency varies. Message and data rates may apply.',
      'You may opt out of SMS messages at any time by replying STOP to any CheckInCare text message. For help with CheckInCare SMS messages, reply HELP to a CheckInCare text message.',
      'Carriers are not liable for delayed or undelivered messages.',
      'You may provide only mobile phone numbers for which you are the subscriber, customary user, or otherwise legally authorized to provide consent. You are solely responsible for providing and maintaining a valid, current, SMS-capable mobile phone number.',
      'Where SMS notifications are optional, consent to receive SMS messages is not a condition of purchasing the device or subscribing to the Services.',
      'Your use of SMS functionality is also subject to the CheckInCare Privacy Policy.'
    ]
  },
  {
    title: '10. User Content, Contact Information, and Permissions',
    paragraphs: [
      'You may submit information through the Services, including names, email addresses, phone numbers, caregiver contact details, account information, settings, and other content or data.',
      'You retain ownership of your User Content, but you grant CheckInCare a worldwide, non-exclusive, royalty-free license to use, host, store, reproduce, transmit, modify, and process User Content as reasonably necessary to operate, support, improve, and provide the Services.'
    ]
  },
  {
    title: '11. Acceptable Use Policy',
    bullets: [
      'Use the Services for any unlawful, fraudulent, abusive, deceptive, harassing, or harmful purpose',
      'Use the Services in connection with emergencies or where failure or delay could reasonably lead to death, bodily injury, or property damage',
      'Upload or transmit malicious code, malware, spyware, viruses, or harmful content',
      'Attempt to gain unauthorized access to any account, device, network, or data',
      'Use another person\'s account, identity, phone number, or contact information without authorization',
      'Send messages to any recipient without appropriate consent'
    ],
    lead: 'You agree not to, and not to permit any third party to:'
  },
  {
    title: '12. Intellectual Property',
    paragraphs: [
      'The Services, including all software, firmware, device designs, interfaces, trademarks, logos, content, compilations, audiovisual works, text, graphics, and technology, are owned by CheckInCare or its licensors and are protected by intellectual property and other laws.',
      'Subject to your compliance with these Terms, CheckInCare grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable right to access and use the Services solely for your personal or internal lawful use in connection with the intended purpose of the Services.'
    ]
  },
  {
    title: '13. Privacy',
    paragraphs: [
      'Your use of the Services is subject to the CheckInCare Privacy Policy, which is incorporated into these Terms by reference.'
    ]
  },
  {
    title: '14. Suspension and Termination',
    paragraphs: [
      'We may suspend, restrict, or terminate your access to some or all of the Services at any time, with or without notice, if you violate these Terms, payment is overdue, required third-party services are unavailable, or we believe suspension is necessary to protect users, caregivers, CheckInCare, third parties, or the integrity of the Services.'
    ]
  },
  {
    title: '15. Disclaimer of Warranties',
    paragraphs: [
      'To the maximum extent permitted by law, the Services are provided "as is," "as available," and with all faults, without warranties of any kind, whether express, implied, statutory, or otherwise.',
      'CheckInCare expressly disclaims all warranties, including any implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, quiet enjoyment, accuracy, reliability, availability, security, or that the Services will be uninterrupted, timely, error-free, or free of harmful components.'
    ]
  },
  {
    title: '16. Limitation of Liability',
    paragraphs: [
      'To the maximum extent permitted by law, CheckInCare and its affiliates, owners, officers, directors, employees, contractors, agents, licensors, suppliers, service providers, and successors shall not be liable for any indirect, incidental, special, exemplary, consequential, or punitive damages, or for any loss of profits, revenue, data, goodwill, business opportunity, or use, arising out of or relating to the Services.',
      'To the maximum extent permitted by law, the total aggregate liability of CheckInCare for all claims arising out of or relating to the Services shall not exceed the greater of the amount you paid to CheckInCare for the Services in the twelve (12) months immediately preceding the event giving rise to the claim or one hundred U.S. dollars (US $100).'
    ]
  },
  {
    title: '17. Indemnification',
    paragraphs: [
      'You agree to defend, indemnify, and hold harmless CheckInCare and its affiliates, owners, officers, directors, employees, contractors, agents, licensors, suppliers, and service providers from and against any and all claims, demands, actions, investigations, liabilities, damages, judgments, settlements, penalties, fines, costs, and expenses, including reasonable attorneys\' fees, arising out of or relating to your use or misuse of the Services, your violation of these Terms, your violation of any law, or your failure to obtain any required consent, authorization, or permission from a recipient, caregiver, or other third party.'
    ]
  },
  {
    title: '18. Dispute Resolution; Binding Arbitration; Class Action Waiver',
    paragraphs: [
      'These Terms and any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be governed by the laws of the State of Texas and the United States, without regard to conflict of law principles.',
      'Except for disputes that qualify for small claims court, disputes relating to intellectual property, or requests for temporary, preliminary, or injunctive relief to protect rights or property, any dispute, claim, or controversy arising out of or relating to these Terms or the Services shall be resolved exclusively by final and binding arbitration on an individual basis.',
      'You and CheckInCare agree that each may bring claims against the other only in an individual capacity and not as a plaintiff, class member, private attorney general, or participant in any purported class, collective, consolidated, mass, or representative action or proceeding.',
      'To the extent any dispute is permitted to proceed in court, you and CheckInCare agree to the exclusive jurisdiction and venue of the state and federal courts located in Texas.'
    ]
  },
  {
    title: '19. Changes to the Services or Terms',
    paragraphs: [
      'CheckInCare may modify these Terms at any time by posting updated Terms on its website or otherwise making them available through the Services. Your continued use of the Services after updated Terms become effective constitutes your acceptance of the revised Terms.'
    ]
  },
  {
    title: '20. Electronic Communications',
    paragraphs: [
      'By using the Services, you consent to receive communications from CheckInCare electronically, including by email, in-app notice, website posting, or SMS where applicable.'
    ]
  },
  {
    title: '21. Assignment',
    paragraphs: [
      'You may not assign or transfer these Terms or any rights or obligations under them without CheckInCare\'s prior written consent. CheckInCare may assign or transfer these Terms, in whole or in part, without restriction.'
    ]
  },
  {
    title: '22. Severability',
    paragraphs: [
      'If any provision of these Terms is held to be invalid, illegal, or unenforceable, that provision shall be enforced to the maximum extent permitted by law, and the remaining provisions shall remain in full force and effect.'
    ]
  },
  {
    title: '23. No Waiver',
    paragraphs: [
      'No waiver by CheckInCare of any provision of these Terms shall be deemed a further or continuing waiver of that or any other provision.'
    ]
  },
  {
    title: '24. Entire Agreement',
    paragraphs: [
      'These Terms, together with the Privacy Policy and any additional terms expressly incorporated by reference, constitute the entire agreement between you and CheckInCare regarding the Services.'
    ]
  },
  {
    title: '25. Acknowledgment',
    paragraphs: [
      'By accessing or using the Services, you acknowledge that you have read, understand, and agree to these Terms, including the important safety disclaimers, warranty disclaimers, limitations of liability, indemnification obligations, binding arbitration provision, and class action waiver.'
    ]
  }
];

function Section({ title, paragraphs = [], bullets = [], lead }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-secondary-900">{title}</h2>
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="text-sm leading-6 text-secondary-600">
          {paragraph}
        </p>
      ))}
      {lead ? <p className="text-sm leading-6 text-secondary-600">{lead}</p> : null}
      {bullets.length ? (
        <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-secondary-600">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 px-4 py-10">
      <div className="mx-auto max-w-4xl rounded-2xl bg-white p-8 shadow-xl">
        <h1 className="text-3xl font-bold text-secondary-900">Terms and Conditions</h1>
        <p className="mt-3 text-sm font-medium text-secondary-500">Last Updated: April 2026</p>
        <p className="mt-4 text-sm leading-6 text-secondary-600">
          These Terms and Conditions govern your access to and use of the CheckInCare website,
          software, connected device, caregiver interfaces, notifications, and related services.
        </p>

        <div className="mt-8 space-y-7">
          {sections.map((section) => (
            <Section key={section.title} {...section} />
          ))}
        </div>
      </div>
    </div>
  );
}
